import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { CloudArrowUpIcon, DocumentIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI';
const RECAPTCHA_BYPASS = process.env.REACT_APP_BYPASS_RECAPTCHA === 'true';

const UpdatePage: React.FC = () => {
  const { uploadLink } = useParams<{ uploadLink: string }>();
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  type FileMeta = { filename: string; file_size: number };
  const [currentFiles, setCurrentFiles] = useState<FileMeta[]>([]);
  const [replacements, setReplacements] = useState<Record<string, File>>({});
  const [loading, setLoading] = useState(false);
  const [loadingMeta, setLoadingMeta] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const refreshLatestFiles = async () => {
    if (!uploadLink) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/api/presentations/${uploadLink}`, { params: { t: Date.now() } });
      const data = res.data;
      const versions = data?.versions || [];
      if (versions.length > 0) {
        const latest = versions[versions.length - 1];
        if (latest.files && Array.isArray(latest.files)) {
          setCurrentFiles(latest.files as FileMeta[]);
        } else if (latest.filename) {
          setCurrentFiles([{ filename: latest.filename, file_size: latest.file_size || 0 }]);
        } else {
          setCurrentFiles([]);
        }
      }
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load current files');
    }
  };

  // Fetch latest version files
  useEffect(() => {
    (async () => {
      await refreshLatestFiles();
      setLoadingMeta(false);
    })();
  }, [uploadLink]);

  const handleReplacementChange = (targetFilename: string, fileList: FileList | null) => {
    if (!fileList || fileList.length === 0) return;
    const f = fileList[0];
    const allowed = ['.pdf', '.pptx', '.pps', '.mp4', '.zip'];
    const ext = f.name.substring(f.name.lastIndexOf('.')).toLowerCase();
    if (!allowed.includes(ext)) {
      setError('Only PDF, PPTX, PPS, MP4, ZIP files are allowed');
      return;
    }
    if (f.size > 250 * 1024 * 1024) {
      setError(`File ${f.name} exceeds 250MB limit`);
      return;
    }
    setReplacements(prev => ({ ...prev, [targetFilename]: f }));
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    const keys = Object.keys(replacements);
    if (keys.length === 0) {
      setError('Please choose at least one file to replace');
      return;
    }
    
    let recaptchaToken: string | null | undefined = null;
    if (RECAPTCHA_BYPASS) {
      recaptchaToken = 'bypass';
    } else {
      try {
        recaptchaToken = await recaptchaRef.current?.executeAsync();
      } catch (e) {
        setError('reCAPTCHA verification failed. Please try again.');
        return;
      } finally {
        recaptchaRef.current?.reset();
      }
      if (!recaptchaToken) {
        setError('reCAPTCHA verification failed. Please try again.');
        return;
      }
    }
    
    setLoading(true);
    
    try {
      const formData = new FormData();
      const targetNames: string[] = [];
      Object.entries(replacements).forEach(([target, file]) => {
        formData.append('files', file);
        targetNames.push(target);
      });
      formData.append('target_names', JSON.stringify(targetNames));
      formData.append('recaptcha_token', recaptchaToken);

      await axios.post(`${API_BASE_URL}/api/upload/${uploadLink}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress: (p) => {
          if (p.total) setUploadProgress(Math.round((p.loaded * 100) / p.total));
        },
      });

      await refreshLatestFiles();
      setSuccess(true);
      setReplacements({});
    } catch (err: any) {
      setError(err.response?.data?.detail || 'An error occurred during upload');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="max-w-2xl w-full bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Update Successful!</h2>
            <p className="text-gray-600 mb-6">
              Your presentation has been updated successfully.
            </p>
            
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-gray-600 mb-2">Updated files in the latest version:</p>
              <ul className="list-disc pl-5 text-sm text-gray-800">
                {currentFiles.map((f) => (
                  <li key={f.filename}>{f.filename}</li>
                ))}
              </ul>
            </div>

            <button
              onClick={() => setSuccess(false)}
              className="btn-primary"
            >
              Upload Another Version
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/')}
            className="flex items-center text-[#00529B] hover:text-[#003D74] mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Upload
          </button>
          
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#00529B] to-[#E31D3C] bg-clip-text text-transparent">
              Update Your Presentation
            </h1>
            <p className="text-gray-600">
              Upload a new version of your presentation
            </p>
          </div>
        </div>

        {/* Update Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Current Files List with replacement inputs */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Replace files in the latest version
              </label>
              {loadingMeta ? (
                <div className="flex justify-center items-center py-8">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#00529B]"></div>
                </div>
              ) : currentFiles.length === 0 ? (
                <p className="text-sm text-gray-600">No files found in the latest version.</p>
              ) : (
                <div className="divide-y divide-gray-200 rounded-lg border border-gray-200">
                  {currentFiles.map((fm) => (
                    <div key={fm.filename} className="p-4 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <DocumentIcon className="h-6 w-6 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-800">{fm.filename}</p>
                          <p className="text-xs text-gray-500">{(fm.file_size / (1024 * 1024)).toFixed(2)} MB</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <input
                          id={`file-${fm.filename}`}
                          type="file"
                          accept=".pdf,.pptx,.pps,.mp4,.zip"
                          className="hidden"
                          onChange={(e) => handleReplacementChange(fm.filename, e.target.files)}
                        />
                        <label
                          htmlFor={`file-${fm.filename}`}
                          className="btn-outline text-sm px-4 py-2 cursor-pointer"
                        >
                          {replacements[fm.filename] ? 'Change selected file' : 'Replace file'}
                        </label>
                        {replacements[fm.filename] && (
                          <span className="text-xs text-gray-600 max-w-[220px] truncate">
                            Selected: {replacements[fm.filename].name}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {loading && (
                <div className="w-full bg-gray-200 rounded-full h-2 mt-4">
                  <div className="bg-[#00529B] h-2 rounded-full" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
              <p className="mt-2 text-xs text-gray-500">Allowed types: PDF, PPTX, PPS, MP4, ZIP. Max 250MB per file.</p>
            </div>

            {/* Important Notice */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-amber-700 font-semibold mb-1">
                    Important: Conference Equipment Only
                  </p>
                  <p className="text-sm text-amber-700">
                    Only conference-provided equipment is allowed. There's no possibility to plugin your own computer into the conference room.
                  </p>
                </div>
              </div>
            </div>

            {/* reCAPTCHA */}
            <div className="flex justify-center">
              <ReCAPTCHA
                ref={recaptchaRef}
                size="invisible"
                sitekey={RECAPTCHA_SITE_KEY}
              />
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 rounded">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-primary text-lg py-4 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Uploading...
                </span>
              ) : (
                'Upload New Version'
              )}
            </button>
          </form>
        </div>

        {/* Data Protection Notice */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Publication / Data Protection
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            Presentations will be published after the conference on the conference website. Please do not include in your
            presentation elements you do not wish to see published. In addition, speakers and attendance will be photographed.
            In submitting a speaker's proposal, you agree that your data may be published on the conference website, via our
            promotional channels and that we may use it for promotional purposes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpdatePage;
